import { User, ApiResponse, AuthState } from "../types";
import debugx from "debugx";
import { friendsService } from "./friendsService";

const debug = debugx("bikedash:auth");

export class AuthService {
  private authPopup: Window | null = null;
  private broadcastChannel: BroadcastChannel | null = null;
  private authStateCallbacks: Set<(state: AuthState) => void> = new Set();

  constructor() {
    // Initialize BroadcastChannel for cross-tab communication
    if (typeof BroadcastChannel !== "undefined") {
      this.broadcastChannel = new BroadcastChannel("bikedash_auth");
      this.broadcastChannel.onmessage = this.handleBroadcastMessage.bind(this);
    }
  }

  private handleBroadcastMessage(event: MessageEvent) {
    debug("BroadcastChannel message:", event.data);

    const message = event.data;
    if (message.type === "auth_complete" && message.status === "success") {
      // Auth completed successfully in popup
      if (this.authPopup && !this.authPopup.closed) {
        this.authPopup.close();
      }

      // We have the user data directly from the broadcast!
      if (message.user) {
        // Use the full user object from server (includes isAdmin, displayName, profile, etc.)
        const user: User = message.user;

        const state: AuthState = {
          isAuthenticated: true,
          user,
          token: null,
          isGuest: false,
          isLoading: false,
          error: null,
        };

        // Cache auth state for offline use (initially without API key)
        localStorage.setItem(
          "bikedash_cached_auth",
          JSON.stringify({
            isAuthenticated: true,
            user,
            isGuest: false,
          }),
        );

        // Also fetch API key from /api/config after successful login
        fetch("/api/config", {
          method: "GET",
          credentials: "include",
        })
          .then((response) => response.json())
          .then((configData) => {
            if (configData.thunderforestApiKey) {
              debug("Thunderforest API key received from /api/config");
              // Update cached auth with API key
              const cachedData = {
                isAuthenticated: true,
                user,
                isGuest: false,
                thunderforestApiKey: configData.thunderforestApiKey,
              };
              localStorage.setItem(
                "bikedash_cached_auth",
                JSON.stringify(cachedData),
              );
            }
          })
          .catch((error) => {
            debug.warn("Failed to fetch API key from /api/config:", error);
          });

        this.notifyAuthStateChange(state);
        debug("User authenticated via broadcast:", user.username);
      } else {
        // Fallback to validate session if no user data
        this.validateSession();
      }
    }
  }

  // Open auth popup window
  openAuthPopup(mode: "login" | "guest" = "login"): void {
    const width = 500;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const url = mode === "guest" ? "/auth/guest" : "/auth/login";

    this.authPopup = window.open(
      url,
      "bikedash-auth",
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`,
    );

    // Also listen for postMessage from popup (fallback for older browsers)
    window.addEventListener("message", this.handlePostMessage.bind(this));

    // Check if popup was blocked
    if (!this.authPopup || this.authPopup.closed) {
      this.notifyError("Popup blocked. Please allow popups for this site.");
      return;
    }
  }

  private handlePostMessage(event: MessageEvent) {
    if (event.data?.type === "bikedash-auth") {
      debug("PostMessage from auth popup:", event.data.message);

      const message = event.data.message;
      if (message.type === "auth_complete" && message.status === "success") {
        if (this.authPopup && !this.authPopup.closed) {
          this.authPopup.close();
        }

        // Handle the same as broadcast message
        this.handleBroadcastMessage({ data: message } as MessageEvent);
      }
    }
  }

  // Validate current session (cookie-based)
  async validateSession(): Promise<AuthState> {
    try {
      // First try to validate the session
      const response = await fetch("/auth/validate", {
        method: "GET",
        credentials: "include", // Include cookies
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (data.authenticated) {
        // Check if we have cached profile data to preserve
        const cachedAuth = localStorage.getItem("bikedash_cached_auth");
        let mergedUser = data.user;

        if (cachedAuth) {
          try {
            const cached = JSON.parse(cachedAuth);
            // Preserve profile fields from cache if they exist
            // This prevents validate from overwriting recently updated profile data
            if (cached.user) {
              mergedUser = {
                ...data.user,
                username: cached.user.username || data.user.username,
                mapIcon:
                  cached.user.mapIcon !== undefined
                    ? cached.user.mapIcon
                    : data.user.mapIcon,
                mapColor: cached.user.mapColor || data.user.mapColor,
              };
            }
          } catch (e) {
            debug.warn("Failed to parse cached auth:", e);
          }
        }

        const state: AuthState = {
          isAuthenticated: true,
          user: mergedUser,
          token: null,
          isGuest: data.isGuest || false,
          isLoading: false,
          error: null,
        };

        // Also fetch API key from /api/config
        try {
          const configResponse = await fetch("/api/config", {
            method: "GET",
            credentials: "include",
          });

          if (configResponse.ok) {
            const configData = await configResponse.json();
            if (configData.thunderforestApiKey) {
              debug("Thunderforest API key received from /api/config");
              // Store in cached auth
              localStorage.setItem(
                "bikedash_cached_auth",
                JSON.stringify({
                  isAuthenticated: true,
                  user: mergedUser,
                  isGuest: data.isGuest || false,
                  thunderforestApiKey: configData.thunderforestApiKey,
                }),
              );

              this.notifyAuthStateChange(state);
              return state;
            }
          }
        } catch (error) {
          debug.warn("Failed to fetch API key from /api/config:", error);
        }

        // Cache auth state for offline use (without API key if not fetched)
        localStorage.setItem(
          "bikedash_cached_auth",
          JSON.stringify({
            isAuthenticated: true,
            user: mergedUser,
            isGuest: data.isGuest || false,
          }),
        );

        this.notifyAuthStateChange(state);
        return state;
      } else {
        const state: AuthState = {
          isAuthenticated: false,
          user: null,
          token: null,
          isGuest: false,
          isLoading: false,
          error: null,
        };

        // Clear cached auth when explicitly not authenticated
        localStorage.removeItem("bikedash_cached_auth");

        this.notifyAuthStateChange(state);
        return state;
      }
    } catch (error) {
      debug.error("Session validation error:", error);

      // When offline, try to use cached auth state
      const cachedAuth = localStorage.getItem("bikedash_cached_auth");
      if (cachedAuth) {
        try {
          const cached = JSON.parse(cachedAuth);
          const state: AuthState = {
            ...cached,
            isLoading: false,
            error: "Offline - using cached credentials",
          };

          this.notifyAuthStateChange(state);
          return state;
        } catch (e) {
          debug.error("Failed to parse cached auth:", e);
        }
      }

      // No cached auth available
      const state: AuthState = {
        isAuthenticated: false,
        user: null,
        token: null,
        isGuest: false,
        isLoading: false,
        error: "Failed to validate session",
      };

      this.notifyAuthStateChange(state);
      return state;
    }
  }

  // Update Profile
  async updateProfile(updates: {
    username?: string;
    mapIcon?: string;
    mapColor?: string;
  }): Promise<void> {
    try {
      let response: Response;
      try {
        response = await fetch("/api/auth/profile", {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updates),
        });
      } catch (networkError) {
        debug.error("Network error updating profile:", networkError);
        throw new Error("Network error - check your connection");
      }

      if (!response.ok) {
        let errorMessage = "Failed to update profile";
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch {
          // If JSON parsing fails, use default message
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      // Update the current user state
      if (result.user) {
        // Get current cached auth state
        const cachedAuth = localStorage.getItem("bikedash_cached_auth");
        let currentState: Partial<AuthState> = {
          isAuthenticated: true,
          isGuest: false,
          isLoading: false,
          error: null,
          token: null,
        };

        if (cachedAuth) {
          try {
            const cached = JSON.parse(cachedAuth);
            currentState = { ...currentState, ...cached };
          } catch (e) {
            debug.warn("Failed to parse cached auth:", e);
          }
        }

        const newState: AuthState = {
          ...currentState,
          user: result.user,
        } as AuthState;

        // Update cached auth
        if (cachedAuth) {
          const cached = JSON.parse(cachedAuth);
          cached.user = result.user;
          localStorage.setItem("bikedash_cached_auth", JSON.stringify(cached));
        }

        this.notifyAuthStateChange(newState);
        debug("Profile updated:", result.user);
      }
    } catch (error) {
      debug.error("Update profile error:", error);
      throw error;
    }
  }

  // Logout
  async logout(): Promise<void> {
    try {
      await fetch("/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      debug.error("Logout error:", error);
    }

    // Clear cached auth on logout
    localStorage.removeItem("bikedash_cached_auth");

    const state: AuthState = {
      isAuthenticated: false,
      user: null,
      token: null,
      isGuest: false,
      isLoading: false,
      error: null,
    };

    this.notifyAuthStateChange(state);
    debug("User logged out, state updated");
  }

  // Old JWT-based methods for backwards compatibility
  async login(
    username: string,
    password: string,
  ): Promise<{ user: User; token: string }> {
    debug("Attempting login for user:", username);

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const data: ApiResponse = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Login failed");
      }

      return data.data;
    } catch (error) {
      debug.error("Login error:", error);
      throw error;
    }
  }

  async validateToken(token: string): Promise<User | null> {
    try {
      const response = await fetch("/api/auth/validate", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        debug.warn("Token validation failed with status:", response.status);
        return null;
      }

      const data: ApiResponse = await response.json();
      return data.success ? data.data : null;
    } catch (error) {
      debug.error("Token validation error:", error);
      return null;
    }
  }

  async refreshToken(currentToken: string): Promise<string> {
    try {
      const response = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });

      const data: ApiResponse = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Token refresh failed");
      }

      return data.data.token;
    } catch (error) {
      debug.error("Token refresh error:", error);
      throw error;
    }
  }

  async register(
    username: string,
    email: string,
    password: string,
  ): Promise<{ user: User; token: string }> {
    debug("Attempting registration for user:", username);

    try {
      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, email, password }),
      });

      const data: ApiResponse = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Registration failed");
      }

      return data.data;
    } catch (error) {
      debug.error("Registration error:", error);
      throw error;
    }
  }

  // Subscribe to auth state changes
  onAuthStateChange(callback: (state: AuthState) => void): () => void {
    this.authStateCallbacks.add(callback);
    return () => {
      this.authStateCallbacks.delete(callback);
    };
  }

  private notifyAuthStateChange(state: AuthState) {
    this.authStateCallbacks.forEach((callback) => callback(state));

    // Start/stop presence updates and background checks based on auth state
    if (state.isAuthenticated && !state.isGuest) {
      friendsService.startPresenceUpdates();
      friendsService.startBackgroundChecks();
    } else {
      friendsService.stopPresenceUpdates();
      friendsService.stopBackgroundChecks();
    }
  }

  private notifyError(error: string) {
    const state: AuthState = {
      isAuthenticated: false,
      user: null,
      token: null,
      isGuest: false,
      isLoading: false,
      error,
    };
    this.notifyAuthStateChange(state);
  }

  // Helper to make authenticated API calls with automatic token refresh
  async authenticatedFetch(
    url: string,
    options: RequestInit = {},
  ): Promise<Response> {
    const token = localStorage.getItem("authToken");
    if (!token) {
      throw new Error("No authentication token");
    }

    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    };

    let response = await fetch(url, { ...options, headers });

    // If we get a 401, try to refresh the token and retry once
    if (response.status === 401) {
      debug("Got 401, attempting token refresh...");
      try {
        const newToken = await this.refreshToken(token);
        localStorage.setItem("authToken", newToken);

        headers["Authorization"] = `Bearer ${newToken}`;
        response = await fetch(url, { ...options, headers });
      } catch (error) {
        debug.error("Token refresh failed, user needs to log in again");
        throw error;
      }
    }

    return response;
  }
}

export const authService = new AuthService();
